import React, { useCallback, useEffect, useState } from "react";
import LoadingIndicator from "react-loading-indicator";
import { CustomerSucessController } from "@talentbait/shared-components";
import { Group } from "../../../../../Components/Layout";
import CampaignCreateTeaserEditor from "../../Campaign__Create_TeaserEditor/Campaign__Create_TeaserEditor.jsx";
import { withRefluxStore } from "../../../../../Stores";
import JobPageStore from "./JobPageStore";
import Actions from "./JobPageActions";
import { optimizeCloudinaryImage } from "../../../../../utils/Helpers";
import {
  JOB_PAGE_IMAGE_SIZE_SPEC,
  PostEditorJobPageWrapper,
  EditorWrapper,
  TextSection,
  Label,
  OpenLeadForm,
  textStyle
} from "./JobPage.styled";
import { useTranslation } from "react-i18next";
import logger from "../../../../../utils/LoggerFrontend.js";
import JobTemplatePost from "./JobTemplatePost.jsx";

export const updateJobPageCallback = ({ updates, campaignId, ...rest }) => {
  const newValues = { ...rest };

  Object.keys(updates).forEach((key) => {
    let value = updates[key];
    if (typeof value === "object" && value.length) value = value[0];
    if (key === "image") value = optimizeCloudinaryImage(value, "fit");
    else if (key === "companyLogo") value = optimizeCloudinaryImage(value, "scale");

    newValues[key] = value;
  });

  Actions.updateJobPage(campaignId, newValues);
};

export const JobPage = ({
  orgaId,
  campaignId,
  companyId,
  jobPageLink = "",
  creatives,
  // JobPageStore
  image = "",
  benefits = "",
  teaserText = "",
  companyLogo = "",
  tasks = "",
  requirements = "",
  isFetchingJobPage = true,
  tbCampaignId,
  mediaAssetId
}) => {
  const { t } = useTranslation();

  const [brandkitLogos, setBrandkitLogos] = useState({});

  const updatePost = ({ creative, creativeAsset, logo, mediaAssetId }) => {
    const updates = {};

    if (creativeAsset && creative) {
      updates.image = creative;
    }
    if (logo) {
      updates.companyLogo = logo;
    }
    if (mediaAssetId) {
      updates.mediaAssetId = mediaAssetId;
    }

    if (Object.keys(updates).length > 0) {
      updateJobPage(updates);
    } else {
      console.warn("No updates provided to updatePost");
    }
  };

  useEffect(() => {
    if (companyId) {
      Actions.getBrandKitByCompanyId(companyId);
      const brandkitSuccessfully = Actions.getBrandKitByCompanyId.completed.listen((result) => {
        const { payload } = result;
        setBrandkitLogos(payload.logos);
        brandkitSuccessfully();
      });
    }
  }, [companyId]);

  useEffect(() => {
    logger.log("JobPage:useEffect", "job page editor view opened", {
      campaignId,
      tbCampaignId,
      campaignHasBeenFetched: campaignId === tbCampaignId,
      mediaAssetId
    });
    CustomerSucessController.send(`JobPageEditor:opened`, { campaignId });

    if (tbCampaignId !== campaignId) {
      Actions.getJobPage(campaignId);
    }
  }, [campaignId, tbCampaignId]);

  const updateJobPage = useCallback(
    (updates) => {
      CustomerSucessController.send(`JobPageEditor:updatedJobPage`, { updates, campaignId });
      logger.log("JobPage:updateJobPage", "updating job page data", {
        campaignId,
        updates,
        image,
        benefits,
        teaserText,
        companyLogo,
        requirements,
        mediaAssetId
      });
      return updateJobPageCallback({
        updates,
        campaignId,
        image,
        benefits,
        teaserText,
        companyLogo,
        tasks,
        requirements,
        mediaAssetId
      });
    },
    [campaignId, image, benefits, teaserText, companyLogo, tasks, requirements, mediaAssetId]
  );

  const backgroundImage = image || creatives?.[0];

  const logos = brandkitLogos ?? {};
  const brandkitLogo = logos.squareLogo || logos.landscapeLogo;
  const jobFormLogo = brandkitLogo || companyLogo;

  if (isFetchingJobPage) {
    return (
      <div>
        {t("Loading")} <br />
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <PostEditorJobPageWrapper>
      <Group justify="space-between" align="center" margin="0 0 10px 0">
        <Label data-label="header-image-label">{t("Main Picture")}</Label>

        <OpenLeadForm href={jobPageLink} target="_blank" rel="noreferrer">
          <i className="fa fa-external-link"></i>
          <p>{t("Open Job Page")}</p>
        </OpenLeadForm>
      </Group>

      <JobTemplatePost
        orgaId={orgaId}
        image={backgroundImage}
        companyLogo={jobFormLogo}
        mediaAssetId={mediaAssetId}
        updatePost={updatePost}
        templatePreviewSizes={JOB_PAGE_IMAGE_SIZE_SPEC}
      />

      <TextSection data-label="about-job-section" mt={20}>
        <Label data-label="about-job-label">{t("About this Job")}</Label>
        <EditorWrapper>
          <CampaignCreateTeaserEditor
            updatePost={updateJobPage}
            teaserText={teaserText}
            teaserTextStyle={textStyle}
            smallButtons
            minRows={4}
            editable={true}
            postDataKey="teaserText"
            showTeaserRecommendations={false}
            showMoreButton={false}
            placeholder={t("Short description")}
          />
        </EditorWrapper>
      </TextSection>
      <TextSection data-label="benefits-section">
        <Label data-label="benefits-label">{t("The Benefits")}</Label>
        <EditorWrapper>
          <CampaignCreateTeaserEditor
            updatePost={updateJobPage}
            teaserText={benefits}
            teaserTextStyle={textStyle}
            smallButtons
            minRows={5}
            editable={true}
            postDataKey="benefits"
            showTeaserRecommendations={false}
            showMoreButton={false}
            placeholder={`✅ ${t("Benefits")}`}
            disableCheckmarks={true}
            allowEmpty={true}
          />
        </EditorWrapper>
      </TextSection>
      <TextSection data-label="tasks-section">
        <Label data-label="tasks-label">{t("The Tasks")}</Label>
        <EditorWrapper>
          <CampaignCreateTeaserEditor
            updatePost={updateJobPage}
            teaserText={tasks}
            teaserTextStyle={textStyle}
            smallButtons
            minRows={5}
            editable={true}
            postDataKey="tasks"
            showTeaserRecommendations={false}
            showMoreButton={false}
            placeholder="✔️ Aufgabe"
            allowEmpty={true}
          />
        </EditorWrapper>
      </TextSection>
      <TextSection data-label="requirements-section">
        <Label data-label="requirements-label">{t("The Requirements")}</Label>
        <EditorWrapper>
          <CampaignCreateTeaserEditor
            updatePost={updateJobPage}
            teaserText={requirements}
            teaserTextStyle={textStyle}
            smallButtons
            minRows={5}
            editable={true}
            postDataKey="requirements"
            showTeaserRecommendations={false}
            showMoreButton={false}
            placeholder={t("Requirements")}
            allowEmpty={true}
          />
        </EditorWrapper>
      </TextSection>
    </PostEditorJobPageWrapper>
  );
};

export default withRefluxStore(JobPage, [JobPageStore]);
